import { useContext, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { DatarouterError } from '@zg-rentals/rental-manager-env';

import { usePropertyConditionsSummary } from '../../../../src/hooks/queries/propertycondition';
import { updateLoadingState } from '../../providers/LoadingMapContext/actions';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';
import { useSelector } from '../../selectors/useSelector';

import EmptyPropertyConditionModule from './components/EmptyPropertyConditionModule';
import PropertyConditionErrorModule from './components/PropertyConditionErrorModule';
import PropertyConditionModule from './components/PropertyConditionModule';

import type { UserPropertySummaryPropertyConditionsReportWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const ConnectedPropertyConditionModule = () => {
  const { listingAlias } = useSelector(
    (state) => ({
      listingAlias: state.properties.currentProperty.listingAlias,
    }),
    shallowEqual,
  );

  const {
    reducerDispatch,
    loadingByComponent: { ConnectedPropertyCondition: isConnectedPropertyConditionModuleLoading },
  } = useContext(LoadingMapContext);

  const { data, isPending, error } = usePropertyConditionsSummary(listingAlias);

  const { title = '', actions = [], description = '', propertyConditions = [] } = data || {};

  useEffect(() => {
    updateLoadingState({
      reducerDispatch,
      data: {
        propertyConditionSummary: isPending,
      },
    });
  }, [isPending]);

  const currentReport = useMemo(
    () =>
      propertyConditions?.filter(
        (propertyCondition: UserPropertySummaryPropertyConditionsReportWebDto) => !propertyCondition.isArchived,
      )?.[0],
    [propertyConditions],
  );

  if (error || ((actions == null || !actions.length) && !isConnectedPropertyConditionModuleLoading)) {
    const errorDetails = DatarouterError.getErrorDetails(error);
    const messageCode = errorDetails?.messageCode ?? data?.messageCode;
    // eslint-disable-next-line constellation/feedback-messages
    const errorFallback = [{ text: 'Sorry, an error occurred.' }];
    const partialMessages = errorDetails?.partialMessages ?? data?.partialMessages ?? errorFallback;

    return (
      <PropertyConditionErrorModule
        isUnsupportedStateError={messageCode === 'unsupportedState'}
        partialErrorMessages={partialMessages}
      />
    );
  }

  return currentReport || isConnectedPropertyConditionModuleLoading ? (
    <PropertyConditionModule
      title={title || 'Property condition report'}
      description={description}
      actionUrl={actions[0]?.url}
      actionText={actions[0]?.text}
      propertyConditionReport={currentReport}
      listingAlias={listingAlias}
    />
  ) : (
    <EmptyPropertyConditionModule
      title={title}
      description={description}
      actionUrl={actions[0].url}
      actionText={actions[0].text}
    />
  );
};

export default ConnectedPropertyConditionModule;
